/**
 * Org Change Log
 * ==============
 *
 * Append-only audit trail for the Global Organization Manager admin page.
 * Every mutation (create / delete / rename org, link broadcaster ↔ media
 * owner, change owner, edit languages, change type) writes one entry here
 * so master-org admins can see what was changed, by whom, and when.
 *
 * Path: `/orgChangeLog/{autoId}`
 *
 * Read access: master-org ADMIN only (enforced by firestore.rules).
 * Write access: same. The collection is intentionally global (not org-
 * scoped) because the page itself manages global org state.
 */

import { addDoc, collection, getDocs, orderBy, query, limit, QueryConstraint, where } from 'firebase/firestore';

export type OrgChangeAction =
  | 'org.create'
  | 'org.delete'
  | 'org.rename'
  | 'org.typeChange'
  | 'org.languagesUpdate'
  | 'broadcaster.create'
  | 'broadcaster.delete'
  | 'broadcaster.parentChange'
  | 'mediaowner.create'
  | 'mediaowner.delete'
  | 'owner.assign'
  | 'owner.revoke';

export interface OrgChangeLogEntry {
  id?: string;
  /** Action key — see OrgChangeAction. */
  action: OrgChangeAction;
  /** Target org id, when the action is org-scoped. */
  orgId?: string | null;
  /** Display name of the target org at the time of the change. */
  orgName?: string | null;
  /** When a parent changed (broadcaster → media owner), this is the new id. */
  parentOrgId?: string | null;
  parentOrgName?: string | null;
  /** Old/new values for diff display when relevant (rename, type change, etc.). */
  before?: any;
  after?: any;
  /** uid of the master-org admin who made the change. */
  actorUid: string;
  actorName?: string | null;
  /** Free-text note shown in the log viewer. Render-ready. */
  message: string;
  createdAt: number;
}

const COLLECTION = 'orgChangeLog';

export async function writeChangeLog(
  db: any,
  entry: Omit<OrgChangeLogEntry, 'id' | 'createdAt'>,
): Promise<string> {
  const payload: OrgChangeLogEntry = {
    ...entry,
    createdAt: Date.now(),
  };
  const ref = await addDoc(collection(db, COLLECTION), payload as any);
  return ref.id;
}

/** Load the most recent change-log entries (default 100). Optionally filter by orgId. */
export async function loadChangeLog(
  db: any,
  opts: { orgId?: string; pageSize?: number } = {},
): Promise<OrgChangeLogEntry[]> {
  const constraints: QueryConstraint[] = [];
  if (opts.orgId) constraints.push(where('orgId', '==', opts.orgId));
  constraints.push(orderBy('createdAt', 'desc'));
  constraints.push(limit(opts.pageSize || 100));
  const snap = await getDocs(query(collection(db, COLLECTION), ...constraints));
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
}
